
import { AIModule } from '../../types';

export const AUTONOMOUS_SYSTEMS_MODULE_2: AIModule = {
  id: 'computer-vision-robotics',
  title: 'Computer Vision for Robotics',
  description: 'Implement computer vision systems for autonomous robots including object detection, recognition, and scene understanding.',
  duration: '5 hours',
  difficulty: 'hard',
  content: `
# Computer Vision for Robotics

Computer vision is essential for autonomous systems to perceive and understand their environment, enabling robots to navigate, manipulate objects, and interact safely with the world.

## Fundamentals of Robot Vision

### Vision System Components

1. **Image Acquisition**: Cameras, sensors, and data capture
2. **Preprocessing**: Noise reduction, calibration, normalization
3. **Feature Extraction**: Edge detection, keypoints, descriptors
4. **Object Detection**: Identifying and localizing objects
5. **Scene Understanding**: Spatial relationships and context

### Camera Models and Calibration

**Pinhole Camera Model**: Mathematical representation of camera projection
**Intrinsic Parameters**: Focal length, principal point, distortion
**Extrinsic Parameters**: Camera position and orientation in world coordinates

## Advanced Vision Processing

\`\`\`python
import cv2
import numpy as np
import matplotlib.pyplot as plt
from typing import List, Tuple, Dict, Optional
from dataclasses import dataclass
import math
from sklearn.cluster import DBSCAN
from scipy import ndimage

@dataclass
class DetectedObject:
    class_name: str
    confidence: float
    bounding_box: Tuple[int, int, int, int]  # x, y, width, height
    center_3d: Optional[Tuple[float, float, float]] = None
    distance: Optional[float] = None

@dataclass
class CameraParameters:
    focal_length_x: float
    focal_length_y: float
    principal_point_x: float
    principal_point_y: float
    distortion_coefficients: np.ndarray
    
class RobotVisionSystem:
    """Comprehensive computer vision system for autonomous robots"""
    
    def __init__(self, camera_params: CameraParameters):
        self.camera_params = camera_params
        self.camera_matrix = np.array([
            [camera_params.focal_length_x, 0, camera_params.principal_point_x],
            [0, camera_params.focal_length_y, camera_params.principal_point_y],
            [0, 0, 1]
        ])
        
        # Pre-trained models (simplified for demonstration)
        self.object_detector = self._load_object_detector()
        self.depth_estimator = self._load_depth_estimator()
        
        # Vision processing pipeline
        self.preprocessing_enabled = True
        self.edge_detection_enabled = True
        self.object_detection_enabled = True
        
    def _load_object_detector(self):
        """Load object detection model (placeholder)"""
        # In practice, this would load a YOLO, SSD, or similar model
        return None
    
    def _load_depth_estimator(self):
        """Load depth estimation model"""
        # In practice, this would load a depth estimation neural network
        return None
    
    def preprocess_image(self, image: np.ndarray) -> np.ndarray:
        """Preprocess input image for vision pipeline"""
        if not self.preprocessing_enabled:
            return image
        
        # Undistort image using camera parameters
        undistorted = cv2.undistort(image, self.camera_matrix, 
                                   self.camera_params.distortion_coefficients)
        
        # Noise reduction
        denoised = cv2.bilateralFilter(undistorted, 9, 75, 75)
        
        # Enhance contrast
        lab = cv2.cvtColor(denoised, cv2.COLOR_BGR2LAB)
        l, a, b = cv2.split(lab)
        clahe = cv2.createCLAHE(clipLimit=2.0, tileGridSize=(8,8))
        l = clahe.apply(l)
        enhanced = cv2.merge([l, a, b])
        enhanced = cv2.cvtColor(enhanced, cv2.COLOR_LAB2BGR)
        
        return enhanced
    
    def detect_edges(self, image: np.ndarray) -> np.ndarray:
        """Advanced edge detection for robot vision"""
        gray = cv2.cvtColor(image, cv2.COLOR_BGR2GRAY)
        
        # Gradient-based edge detection
        grad_x = cv2.Sobel(gray, cv2.CV_64F, 1, 0, ksize=3)
        grad_y = cv2.Sobel(gray, cv2.CV_64F, 0, 1, ksize=3)
        
        # Calculate magnitude and direction
        magnitude = np.sqrt(grad_x**2 + grad_y**2)
        direction = np.arctan2(grad_y, grad_x)
        
        # Non-maximum suppression
        suppressed = self._non_maximum_suppression(magnitude, direction)
        
        # Hysteresis thresholding
        edges = self._hysteresis_thresholding(suppressed, low_threshold=50, high_threshold=150)
        
        return edges
    
    def _non_maximum_suppression(self, magnitude: np.ndarray, direction: np.ndarray) -> np.ndarray:
        """Apply non-maximum suppression to edge magnitude"""
        height, width = magnitude.shape
        suppressed = np.zeros_like(magnitude)
        
        # Convert direction to degrees
        angle = direction * 180.0 / np.pi
        angle[angle < 0] += 180
        
        for i in range(1, height - 1):
            for j in range(1, width - 1):
                q = 255
                r = 255
                
                # Check direction and compare with neighbors
                if (0 <= angle[i,j] < 22.5) or (157.5 <= angle[i,j] <= 180):
                    q = magnitude[i, j+1]
                    r = magnitude[i, j-1]
                elif 22.5 <= angle[i,j] < 67.5:
                    q = magnitude[i+1, j-1]
                    r = magnitude[i-1, j+1]
                elif 67.5 <= angle[i,j] < 112.5:
                    q = magnitude[i+1, j]
                    r = magnitude[i-1, j]
                elif 112.5 <= angle[i,j] < 157.5:
                    q = magnitude[i-1, j-1]
                    r = magnitude[i+1, j+1]
                
                if magnitude[i,j] >= q and magnitude[i,j] >= r:
                    suppressed[i,j] = magnitude[i,j]
        
        return suppressed
    
    def _hysteresis_thresholding(self, image: np.ndarray, low_threshold: float, 
                                high_threshold: float) -> np.ndarray:
        """Apply hysteresis thresholding for edge detection"""
        high_edges = image > high_threshold
        low_edges = image > low_threshold
        
        # Use morphological operations to connect edges
        kernel = np.ones((3,3), np.uint8)
        connected = cv2.morphologyEx(high_edges.astype(np.uint8), cv2.MORPH_CLOSE, kernel)
        
        # Keep low edges connected to high edges
        final_edges = np.logical_and(low_edges, connected).astype(np.uint8) * 255
        
        return final_edges
    
    def detect_objects(self, image: np.ndarray) -> List[DetectedObject]:
        """Detect and classify objects in the image"""
        # Simplified object detection (would use deep learning in practice)
        detected_objects = []
        
        # Convert to different color spaces for better detection
        hsv = cv2.cvtColor(image, cv2.COLOR_BGR2HSV)
        gray = cv2.cvtColor(image, cv2.COLOR_BGR2GRAY)
        
        # Detect circular objects (wheels, balls, etc.)
        circles = cv2.HoughCircles(gray, cv2.HOUGH_GRADIENT, dp=1, minDist=50,
                                  param1=50, param2=30, minRadius=10, maxRadius=100)
        
        if circles is not None:
            circles = np.round(circles[0, :]).astype("int")
            for (x, y, r) in circles:
                detected_objects.append(DetectedObject(
                    class_name="circular_object",
                    confidence=0.8,
                    bounding_box=(x-r, y-r, 2*r, 2*r)
                ))
        
        # Detect rectangular objects using contour analysis
        edges = cv2.Canny(gray, 50, 150)
        contours, _ = cv2.findContours(edges, cv2.RETR_EXTERNAL, cv2.CHAIN_APPROX_SIMPLE)
        
        for contour in contours:
            if cv2.contourArea(contour) > 1000:  # Filter small contours
                # Approximate contour to polygon
                epsilon = 0.02 * cv2.arcLength(contour, True)
                approx = cv2.approxPolyDP(contour, epsilon, True)
                
                if len(approx) == 4:  # Rectangular object
                    x, y, w, h = cv2.boundingRect(contour)
                    detected_objects.append(DetectedObject(
                        class_name="rectangular_object",
                        confidence=0.7,
                        bounding_box=(x, y, w, h)
                    ))
        
        # Color-based detection for specific objects
        # Detect red objects (stop signs, fire extinguishers, etc.)
        red_objects = self._detect_by_color(hsv, "red")
        detected_objects.extend(red_objects)
        
        # Detect green objects (exit signs, vegetation, etc.)
        green_objects = self._detect_by_color(hsv, "green")
        detected_objects.extend(green_objects)
        
        return detected_objects
    
    def _detect_by_color(self, hsv_image: np.ndarray, color: str) -> List[DetectedObject]:
        """Detect objects by color in HSV space"""
        detected = []
        
        if color == "red":
            # Red has two ranges in HSV
            lower_red1 = np.array([0, 120, 70])
            upper_red1 = np.array([10, 255, 255])
            lower_red2 = np.array([170, 120, 70])
            upper_red2 = np.array([180, 255, 255])
            
            mask1 = cv2.inRange(hsv_image, lower_red1, upper_red1)
            mask2 = cv2.inRange(hsv_image, lower_red2, upper_red2)
            mask = mask1 + mask2
            
        elif color == "green":
            lower_green = np.array([40, 40, 40])
            upper_green = np.array([80, 255, 255])
            mask = cv2.inRange(hsv_image, lower_green, upper_green)
        
        else:
            return detected
        
        # Find contours in the mask
        contours, _ = cv2.findContours(mask, cv2.RETR_EXTERNAL, cv2.CHAIN_APPROX_SIMPLE)
        
        for contour in contours:
            if cv2.contourArea(contour) > 500:  # Filter small areas
                x, y, w, h = cv2.boundingRect(contour)
                detected.append(DetectedObject(
                    class_name=f"{color}_object",
                    confidence=0.6,
                    bounding_box=(x, y, w, h)
                ))
        
        return detected
    
    def estimate_depth(self, image: np.ndarray, detected_objects: List[DetectedObject]) -> List[DetectedObject]:
        """Estimate depth for detected objects"""
        # Simple depth estimation based on object size (monocular)
        # In practice, would use stereo vision or deep learning depth estimation
        
        for obj in detected_objects:
            x, y, w, h = obj.bounding_box
            
            # Assume known object sizes for depth estimation
            if obj.class_name == "circular_object":
                # Assume circular objects are wheels (diameter ~0.6m)
                real_diameter = 0.6  # meters
                pixel_diameter = max(w, h)
                focal_length = self.camera_params.focal_length_x
                
                if pixel_diameter > 0:
                    distance = (real_diameter * focal_length) / pixel_diameter
                    obj.distance = distance
                    
                    # Calculate 3D position
                    center_x = x + w // 2
                    center_y = y + h // 2
                    
                    # Convert pixel coordinates to world coordinates
                    world_x = (center_x - self.camera_params.principal_point_x) * distance / focal_length
                    world_y = (center_y - self.camera_params.principal_point_y) * distance / focal_length
                    world_z = distance
                    
                    obj.center_3d = (world_x, world_y, world_z)
            
            elif obj.class_name == "rectangular_object":
                # Estimate based on typical door/window sizes
                real_height = 2.0  # meters (typical door height)
                pixel_height = h
                focal_length = self.camera_params.focal_length_y
                
                if pixel_height > 0:
                    distance = (real_height * focal_length) / pixel_height
                    obj.distance = distance
        
        return detected_objects
    
    def track_objects(self, current_objects: List[DetectedObject], 
                     previous_objects: List[DetectedObject]) -> List[DetectedObject]:
        """Track objects between frames for motion estimation"""
        if not previous_objects:
            return current_objects
        
        tracked_objects = []
        
        for current_obj in current_objects:
            best_match = None
            best_distance = float('inf')
            
            curr_center = (current_obj.bounding_box[0] + current_obj.bounding_box[2] // 2,
                          current_obj.bounding_box[1] + current_obj.bounding_box[3] // 2)
            
            # Find closest object of same class
            for prev_obj in previous_objects:
                if prev_obj.class_name == current_obj.class_name:
                    prev_center = (prev_obj.bounding_box[0] + prev_obj.bounding_box[2] // 2,
                                  prev_obj.bounding_box[1] + prev_obj.bounding_box[3] // 2)
                    
                    distance = math.sqrt((curr_center[0] - prev_center[0])**2 + 
                                       (curr_center[1] - prev_center[1])**2)
                    
                    if distance < best_distance and distance < 100:  # Max tracking distance
                        best_distance = distance
                        best_match = prev_obj
            
            # Copy tracking information
            if best_match:
                current_obj.distance = best_match.distance
                current_obj.center_3d = best_match.center_3d
            
            tracked_objects.append(current_obj)
        
        return tracked_objects
    
    def process_frame(self, image: np.ndarray, 
                     previous_objects: Optional[List[DetectedObject]] = None) -> Dict:
        """Complete vision processing pipeline"""
        results = {
            'original_image': image,
            'preprocessed_image': None,
            'edges': None,
            'detected_objects': [],
            'annotated_image': None
        }
        
        # Preprocessing
        preprocessed = self.preprocess_image(image)
        results['preprocessed_image'] = preprocessed
        
        # Edge detection
        if self.edge_detection_enabled:
            edges = self.detect_edges(preprocessed)
            results['edges'] = edges
        
        # Object detection
        if self.object_detection_enabled:
            detected_objects = self.detect_objects(preprocessed)
            
            # Depth estimation
            detected_objects = self.estimate_depth(preprocessed, detected_objects)
            
            # Object tracking
            if previous_objects:
                detected_objects = self.track_objects(detected_objects, previous_objects)
            
            results['detected_objects'] = detected_objects
            
            # Create annotated image
            annotated = self._annotate_image(image.copy(), detected_objects)
            results['annotated_image'] = annotated
        
        return results
    
    def _annotate_image(self, image: np.ndarray, objects: List[DetectedObject]) -> np.ndarray:
        """Draw bounding boxes and labels on image"""
        for obj in objects:
            x, y, w, h = obj.bounding_box
            
            # Draw bounding box
            color = (0, 255, 0) if obj.confidence > 0.7 else (0, 255, 255)
            cv2.rectangle(image, (x, y), (x + w, y + h), color, 2)
            
            # Draw label
            label = f"{obj.class_name}: {obj.confidence:.2f}"
            if obj.distance:
                label += f" ({obj.distance:.1f}m)"
            
            cv2.putText(image, label, (x, y - 10), cv2.FONT_HERSHEY_SIMPLEX, 
                       0.6, color, 2)
            
            # Draw center point
            center_x = x + w // 2
            center_y = y + h // 2
            cv2.circle(image, (center_x, center_y), 5, (255, 0, 0), -1)
        
        return image

class StereoVisionSystem:
    """Stereo vision system for accurate depth perception"""
    
    def __init__(self, left_camera_params: CameraParameters, 
                 right_camera_params: CameraParameters,
                 baseline: float):  # Distance between cameras
        self.left_params = left_camera_params
        self.right_params = right_camera_params
        self.baseline = baseline
        
        # Stereo matching parameters
        self.stereo_matcher = cv2.StereoSGBM_create(
            minDisparity=0,
            numDisparities=64,  # Must be divisible by 16
            blockSize=11,
            P1=8 * 3 * 11**2,
            P2=32 * 3 * 11**2,
            disp12MaxDiff=1,
            uniquenessRatio=15,
            speckleWindowSize=0,
            speckleRange=2,
            preFilterCap=63,
            mode=cv2.STEREO_SGBM_MODE_SGBM_3WAY
        )
    
    def compute_disparity(self, left_image: np.ndarray, right_image: np.ndarray) -> np.ndarray:
        """Compute disparity map from stereo image pair"""
        # Convert to grayscale
        left_gray = cv2.cvtColor(left_image, cv2.COLOR_BGR2GRAY)
        right_gray = cv2.cvtColor(right_image, cv2.COLOR_BGR2GRAY)
        
        # Compute disparity
        disparity = self.stereo_matcher.compute(left_gray, right_gray).astype(np.float32) / 16.0
        
        return disparity
    
    def disparity_to_depth(self, disparity: np.ndarray) -> np.ndarray:
        """Convert disparity map to depth map"""
        # Avoid division by zero
        disparity[disparity == 0] = 0.1
        
        # Calculate depth using stereo geometry
        focal_length = self.left_params.focal_length_x
        depth = (focal_length * self.baseline) / disparity
        
        return depth
    
    def get_3d_points(self, disparity: np.ndarray) -> np.ndarray:
        """Generate 3D point cloud from disparity map"""
        height, width = disparity.shape
        
        # Create coordinate grids
        x, y = np.meshgrid(np.arange(width), np.arange(height))
        
        # Calculate 3D coordinates
        focal_length = self.left_params.focal_length_x
        cx = self.left_params.principal_point_x
        cy = self.left_params.principal_point_y
        
        # Avoid division by zero
        valid_disparity = disparity > 0
        
        Z = np.zeros_like(disparity)
        Z[valid_disparity] = (focal_length * self.baseline) / disparity[valid_disparity]
        
        X = (x - cx) * Z / focal_length
        Y = (y - cy) * Z / focal_length
        
        # Stack coordinates
        points_3d = np.stack([X, Y, Z], axis=2)
        
        return points_3d

# Example usage and demonstration
def demonstrate_robot_vision():
    """Demonstrate robot vision system capabilities"""
    
    print("=== Robot Vision System Demo ===")
    
    # Create camera parameters (example values)
    camera_params = CameraParameters(
        focal_length_x=800.0,
        focal_length_y=800.0,
        principal_point_x=320.0,
        principal_point_y=240.0,
        distortion_coefficients=np.array([0.1, -0.2, 0.0, 0.0, 0.0])
    )
    
    # Create vision system
    vision_system = RobotVisionSystem(camera_params)
    
    # Create synthetic test image
    test_image = np.zeros((480, 640, 3), dtype=np.uint8)
    
    # Add some test objects
    # Red rectangle (stop sign simulation)
    cv2.rectangle(test_image, (100, 100), (200, 200), (0, 0, 255), -1)
    
    # Green circle (traffic light simulation)
    cv2.circle(test_image, (400, 150), 50, (0, 255, 0), -1)
    
    # Blue rectangle (door simulation)
    cv2.rectangle(test_image, (300, 200), (400, 400), (255, 0, 0), -1)
    
    # Add some noise
    noise = np.random.randint(0, 50, test_image.shape, dtype=np.uint8)
    test_image = cv2.add(test_image, noise)
    
    print("Processing test image...")
    
    # Process the frame
    results = vision_system.process_frame(test_image)
    
    print(f"Detected {len(results['detected_objects'])} objects:")
    
    for i, obj in enumerate(results['detected_objects']):
        print(f"  Object {i+1}:")
        print(f"    Class: {obj.class_name}")
        print(f"    Confidence: {obj.confidence:.2f}")
        print(f"    Bounding box: {obj.bounding_box}")
        if obj.distance:
            print(f"    Distance: {obj.distance:.2f}m")
        if obj.center_3d:
            print(f"    3D position: ({obj.center_3d[0]:.2f}, {obj.center_3d[1]:.2f}, {obj.center_3d[2]:.2f})")
    
    # Demonstrate stereo vision
    print("\\n=== Stereo Vision Demo ===")
    
    stereo_system = StereoVisionSystem(
        camera_params, camera_params, baseline=0.12  # 12cm baseline
    )
    
    # Create synthetic stereo pair
    left_image = test_image.copy()
    right_image = test_image.copy()
    
    # Simulate disparity by shifting right image
    M = np.float32([[1, 0, -10], [0, 1, 0]])  # 10 pixel shift
    right_image = cv2.warpAffine(right_image, M, (right_image.shape[1], right_image.shape[0]))
    
    # Compute disparity
    disparity = stereo_system.compute_disparity(left_image, right_image)
    depth = stereo_system.disparity_to_depth(disparity)
    
    print(f"Disparity map shape: {disparity.shape}")
    print(f"Depth range: {np.min(depth[depth > 0]):.2f}m to {np.max(depth[depth < 100]):.2f}m")
    
    # Generate 3D point cloud
    points_3d = stereo_system.get_3d_points(disparity)
    print(f"Generated 3D point cloud with shape: {points_3d.shape}")

# Run demonstration
demonstrate_robot_vision()
\`\`\`

## Real-Time Vision Processing

### Performance Optimization
- GPU acceleration with CUDA
- Multi-threading for parallel processing
- Frame skipping for time-critical applications
- Region of Interest (ROI) processing

### Sensor Fusion
- Combining camera data with LiDAR
- Integration with IMU for motion compensation
- Radar data for robust detection
- Multi-spectral imaging

### Deep Learning Integration
- Real-time object detection (YOLO, SSD)
- Semantic segmentation for scene understanding
- Depth estimation networks
- Visual-inertial odometry

Computer vision is fundamental to autonomous systems, providing the "eyes" that enable robots to perceive, understand, and interact with their environment safely and effectively.
  `,
  quiz: {
    id: 'computer-vision-robotics-quiz',
    title: 'Computer Vision for Robotics Quiz',
    passThreshold: 80,
    questions: [
      {
        id: 'q1',
        question: 'What is the primary advantage of stereo vision over monocular vision?',
        type: 'multiple-choice',
        options: [
          'Better color perception',
          'Higher resolution images',
          'Accurate depth perception',
          'Faster processing speed'
        ],
        correctAnswer: 2,
        explanation: 'Stereo vision uses two cameras to calculate depth through triangulation, providing accurate distance measurements essential for robot navigation and manipulation.',
        difficulty: 'hard',
        points: 20
      },
      {
        id: 'q2',
        question: 'What is the purpose of camera calibration in robotic vision?',
        type: 'multiple-choice',
        options: [
          'To improve image brightness',
          'To correct lens distortion and determine camera parameters',
          'To increase frame rate',
          'To reduce computational load'
        ],
        correctAnswer: 1,
        explanation: 'Camera calibration determines intrinsic parameters (focal length, principal point) and corrects distortions, enabling accurate geometric measurements from images.',
        difficulty: 'hard',
        points: 15
      }
    ]
  }
};
